import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit, Trash2, Shield, User, Mail, Calendar, CheckCircle, XCircle } from 'lucide-react';
import { 
  getAdmins, 
  createAdmin, 
  updateAdmin, 
  deleteAdmin 
} from '../lib/pocketbase';
import { Admin, AdminForm } from '../types';
import { formatPersianDate } from '../lib/utils';
import { Table, TableHeader, TableRow, TableCell, TableHeaderCell } from '../components/ui/Table';
import Input from '../components/ui/Input';
import Checkbox from '../components/ui/Checkbox';

const AdminsPage: React.FC = () => {
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState<Admin | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [perPage] = useState(10);

  const [formData, setFormData] = useState<AdminForm>({
    email: '',
    password: '',
    access: [],
    is_super_admin: false
  });

  const [errors, setErrors] = useState<Partial<AdminForm>>({});

  useEffect(() => {
    fetchAdmins();
  }, [currentPage]);

  const fetchAdmins = useCallback(async () => {
    try {
      const response = await getAdmins(currentPage, perPage);
      setAdmins(response.items);
      setTotalPages(response.totalPages);
    } catch (error: any) {
      if (error?.message?.includes('autocancelled')) {
        return;
      }
      console.error('Error fetching admins:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, perPage]);

  const validateForm = (): boolean => {
    const newErrors: Partial<AdminForm> = {};

    if (!formData.email) {
      newErrors.email = 'ایمیل الزامی است';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'ایمیل معتبر نیست';
    }

    if (!editingAdmin && !formData.password) {
      newErrors.password = 'رمز عبور الزامی است';
    } else if (formData.password && formData.password.length < 8) {
      newErrors.password = 'رمز عبور باید حداقل 8 کاراکتر باشد';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const adminData: any = {
        email: formData.email,
        access: formData.access,
        is_super_admin: formData.is_super_admin
      };

      if (!editingAdmin || formData.password) {
        adminData.password = formData.password;
        adminData.passwordConfirm = formData.password;
      }

      if (editingAdmin) {
        await updateAdmin(editingAdmin.id, adminData);
      } else {
        await createAdmin(adminData);
      }

      setShowModal(false);
      setEditingAdmin(null);
      resetForm();
      fetchAdmins();
    } catch (error: any) {
      console.error('Error saving admin:', error);
      if (error.data?.email?.message) {
        setErrors({ email: error.data.email.message });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (admin: Admin) => {
    setEditingAdmin(admin);
    setFormData({
      email: admin.email,
      password: '',
      access: admin.access || [],
      is_super_admin: admin.is_super_admin || false
    });
    setErrors({});
    setShowModal(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('آیا از حذف این مدیر اطمینان دارید؟')) {
      try {
        await deleteAdmin(id);
        fetchAdmins();
      } catch (error) {
        console.error('Error deleting admin:', error);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      access: [],
      is_super_admin: false
    });
    setErrors({});
  };

  const handleAccessChange = (access: string, checked: boolean) => {
    const newAccess = checked
      ? [...(formData.access || []), access as any]
      : (formData.access || []).filter(a => a !== access);
    
    setFormData({ ...formData, access: newAccess });
  };

  const accessOptions = [
    { value: 'products', label: 'محصولات', icon: '📦' },
    { value: 'category', label: 'دسته‌بندی‌ها', icon: '📁' },
    { value: 'chat', label: 'چت', icon: '💬' },
    { value: 'orders', label: 'سفارشات', icon: '🛒' },
    { value: 'users', label: 'کاربران', icon: '👥' }
  ];

  const getAccessLabels = (access: string[] | undefined): string[] => {
    if (!access) return [];
    return access.map(a => {
      const option = accessOptions.find(opt => opt.value === a);
      return option ? `${option.icon} ${option.label}` : a;
    });
  };

  if (loading && admins.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">مدیریت مدیران</h1>
          <p className="text-gray-600">ایجاد و مدیریت دسترسی‌های مدیران</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="btn-primary flex items-center justify-center w-full sm:w-auto"
        >
          <Plus className="h-4 w-4 ml-2" />
          افزودن مدیر
        </button>
      </div>

      {/* Admins Table */}
      <div className="card">
        <Table>
          <TableHeader>
            <TableHeaderCell>ایمیل</TableHeaderCell>
            <TableHeaderCell>وضعیت</TableHeaderCell>
            <TableHeaderCell>دسترسی‌ها</TableHeaderCell>
            <TableHeaderCell>نوع مدیر</TableHeaderCell>
            <TableHeaderCell>تاریخ ایجاد</TableHeaderCell>
            <TableHeaderCell>عملیات</TableHeaderCell>
          </TableHeader>
          <tbody className="bg-white divide-y divide-gray-200">
            {admins.map((admin) => (
              <TableRow key={admin.id}>
                <TableCell>
                  <div className="flex items-center">
                    <Mail className="h-4 w-4 text-gray-400 ml-2" />
                    <span className="font-medium text-gray-900">{admin.email}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center">
                    {admin.verified ? (
                      <CheckCircle className="h-4 w-4 text-green-500 ml-1" />
                    ) : (
                      <XCircle className="h-4 w-4 text-red-500 ml-1" />
                    )}
                    <span className={`text-sm ${admin.verified ? 'text-green-600' : 'text-red-600'}`}>
                      {admin.verified ? 'تایید شده' : 'تایید نشده'}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {getAccessLabels(admin.access).map((label, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full"
                      >
                        {label}
                      </span>
                    ))}
                    {(!admin.access || admin.access.length === 0) && (
                      <span className="text-gray-400 text-sm">بدون دسترسی</span>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center">
                    {admin.is_super_admin ? (
                      <Shield className="h-4 w-4 text-purple-500 ml-1" />
                    ) : (
                      <User className="h-4 w-4 text-gray-500 ml-1" />
                    )}
                    <span className={`text-sm ${admin.is_super_admin ? 'text-purple-600 font-medium' : 'text-gray-600'}`}>
                      {admin.is_super_admin ? 'مدیر ارشد' : 'مدیر عادی'}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center">
                    <Calendar className="h-4 w-4 text-gray-400 ml-1" />
                    <span className="text-sm text-gray-500">
                      {formatPersianDate(admin.created)}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center space-x-2 space-x-reverse">
                    <button
                      onClick={() => handleEdit(admin)}
                      className="p-1 text-primary-600 hover:text-primary-700 transition-colors"
                      title="ویرایش"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(admin.id)}
                      className="p-1 text-red-600 hover:text-red-700 transition-colors"
                      title="حذف"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </tbody>
        </Table>
      </div>

      {/* Empty State */}
      {admins.length === 0 && !loading && (
        <div className="text-center py-12">
          <User className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">هیچ مدیری یافت نشد</h3>
          <p className="mt-1 text-sm text-gray-500">شروع به ایجاد مدیر کنید</p>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center overflow-x-auto">
          <nav className="flex items-center space-x-2 space-x-reverse">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="btn-secondary disabled:opacity-50"
            >
              قبلی
            </button>
            
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`px-3 py-2 text-sm font-medium rounded-lg shrink-0 ${
                  page === currentPage
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                }`}
              >
                {page}
              </button>
            ))}
            
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="btn-secondary disabled:opacity-50"
            >
              بعدی
            </button>
          </nav>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-4 sm:top-20 mx-auto p-4 sm:p-5 border w-[calc(100%-2rem)] max-w-2xl shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingAdmin ? 'ویرایش مدیر' : 'افزودن مدیر جدید'}
              </h3>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <Input
                  label="ایمیل"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  error={errors.email}
                  required
                />
                
                <Input
                  label={editingAdmin ? 'رمز عبور جدید (اختیاری)' : 'رمز عبور'}
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  error={errors.password}
                  required={!editingAdmin}
                  helperText={editingAdmin ? 'برای تغییر رمز عبور، فیلد را پر کنید' : 'حداقل 8 کاراکتر'}
                />
                
                <div className="space-y-4">
                  <label className="block text-sm font-medium text-gray-700">
                    دسترسی‌ها
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {accessOptions.map((option) => (
                      <Checkbox
                        key={option.value}
                        label={`${option.icon} ${option.label}`}
                        checked={(formData.access || []).includes(option.value as any)}
                        onChange={(checked) => handleAccessChange(option.value, checked)}
                      />
                    ))}
                  </div>
                </div>
                
                <Checkbox
                  label="مدیر ارشد (دسترسی کامل به همه بخش‌ها)"
                  checked={formData.is_super_admin || false}
                  onChange={(checked) => setFormData({ ...formData, is_super_admin: checked })}
                />
                
                <div className="flex justify-end space-x-3 space-x-reverse">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingAdmin(null);
                      resetForm();
                    }}
                    className="btn-secondary"
                  >
                    انصراف
                  </button>
                  <button type="submit" className="btn-primary" disabled={loading}>
                    {loading ? 'در حال ذخیره...' : (editingAdmin ? 'ویرایش' : 'افزودن')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminsPage; 